/**
 * The auth seam — docs/spec/02 §2, 06 R1/§5.
 *
 * The ONE place either issuer's URL, request shape, or response shape appears.
 * The client sends an identical request in every environment; only the base URL
 * differs (confirmed with the api team 2026-07-29).
 *
 * These calls deliberately use a bare `fetch`, not `ApiClient`: the refresh call
 * must never pass through the 401 refresh-and-retry interceptor, or a failing
 * refresh would try to refresh itself. Keeping auth off that path makes the
 * recursion structurally impossible rather than guarded against.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { StateStorage } from 'zustand/middleware'
import { getConfig, type AppConfig } from '../app/config'
import { decodeToken, isAdmin, isExpiringSoon, isTranscriber, type TokenClaims } from './jwt'
import { NetworkError } from './errors'

/**
 * Login credentials. The field is **`did`** and its value is the phone number —
 * not `phone`/`phone_number`. Send E.164 with a leading `+`; dev/staging
 * normalises punctuation, so `+1 (702) 779-9066` and `17027799066` are the same
 * user.
 */
export interface Credential {
  readonly did: string
  readonly password: string
}

export interface AuthUser {
  readonly id: string | null
  readonly uuid: string | null
  readonly did: string | null
  readonly name: string | null
  /** Roles as reported by the LOGIN RESPONSE (see the note on `login`). */
  readonly responseRoles: readonly string[]
}

export interface AuthSession {
  readonly accessToken: string
  readonly refreshToken: string
  readonly user: AuthUser
}

/**
 * Login failed. Deliberately one error for every non-2xx.
 *
 * Dev/staging answers a bad credential with a **500-shaped envelope** and
 * production with a **422 `UserBadPassword`**, so the client cannot reliably
 * tell "wrong password" from "api is down" at this endpoint. Per the api team,
 * both are treated as a login failure.
 *
 * The trade-off, stated plainly: a genuine outage during login reads as
 * "invalid phone or password". That is the accepted cost of not showing a
 * transcriber the 5xx "your work was not lost" copy when they simply mistyped —
 * and unlike the submit path, nothing is lost by retrying a login.
 */
export class LoginFailedError extends Error {
  readonly status: number

  constructor(status: number) {
    super('Invalid phone or password.')
    this.name = 'LoginFailedError'
    this.status = status
  }
}

/** The refresh token was rejected — the session is over (06 R1 step 4). */
export class RefreshFailedError extends Error {
  constructor() {
    super('Your session expired — please sign in again.')
    this.name = 'RefreshFailedError'
  }
}

export interface IssuerUrls {
  readonly login: string
  readonly refresh: string
}

/**
 * Which issuer to talk to — 02 §2.
 *
 * Production uses the central Pigeonly issuer on a different host. Everywhere
 * else uses the physical-mail api's own endpoint, which lives *under the
 * transcriptions base* as a sibling of `/jobs` — `{apiBase}/api/v1/auth`, not a
 * top-level path. Local and staging cannot use the production issuer at all:
 * the browser gets a CORS error, which is why this split exists.
 */
export function issuerUrls(config: AppConfig): IssuerUrls {
  if (config.env === 'production') {
    return { login: `${config.coreApiBase}/auth`, refresh: `${config.coreApiBase}/auth/refresh` }
  }
  return {
    login: `${config.apiBase}/api/v1/auth`,
    refresh: `${config.apiBase}/api/v1/auth/refresh`,
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key]
  if (typeof value === 'string' && value.trim() !== '') return value
  // `id` arrives as a number in the login response but a decimal string in the
  // token; normalise so callers never have to care which they are holding.
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

async function postJson(
  url: string,
  body: unknown,
  headers: Readonly<Record<string, string>> = {},
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<{ status: number; ok: boolean; json: unknown }> {
  let response: Response
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...headers },
      body: JSON.stringify(body),
    })
  } catch (cause) {
    throw new NetworkError(undefined, { cause })
  }

  let json: unknown = null
  try {
    const text = await response.text()
    json = text.trim() === '' ? null : JSON.parse(text)
  } catch {
    // A non-JSON body is not itself the failure; the status still decides.
    json = null
  }
  return { status: response.status, ok: response.ok, json }
}

/**
 * Exchange a credential for a session.
 *
 * NOTE on roles: the login response carries a `roles` array, and so does the
 * access token's `identity` claim. **Gating reads the token** (02 §2) — it is
 * what the api itself verifies. The response roles are kept on the user object
 * only so a disagreement between the two is visible rather than silent; if they
 * ever diverge, the token wins and the divergence is a bug to report.
 */
export async function login(
  credential: Credential,
  config: AppConfig = getConfig(),
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<AuthSession> {
  const { status, ok, json } = await postJson(
    issuerUrls(config).login,
    { did: credential.did, password: credential.password },
    {},
    fetchImpl,
  )

  if (!ok) throw new LoginFailedError(status)

  const body = asRecord(json)
  const accessToken = readString(body, 'access_token')
  const refreshToken = readString(body, 'refresh_token')

  // A 200 without tokens is not a success we can act on.
  if (accessToken === null || refreshToken === null) throw new LoginFailedError(status)

  const rawRoles = body['roles']
  return {
    accessToken,
    refreshToken,
    user: {
      id: readString(body, 'id'),
      uuid: readString(body, 'user_uuid'),
      did: readString(body, 'did'),
      name:
        [readString(body, 'first_name'), readString(body, 'last_name')]
          .filter((p): p is string => p !== null)
          .join(' ') || null,
      responseRoles: Array.isArray(rawRoles)
        ? rawRoles.filter((r): r is string => typeof r === 'string')
        : [],
    },
  }
}

/**
 * Exchange a refresh token for a fresh access token.
 *
 * The refresh token travels **both** as `Authorization: Bearer` and in the body
 * — production reads the header, dev/staging accepts either, and sending both
 * means one call shape works everywhere.
 *
 * Returns ONLY a new access token: this endpoint does not rotate the refresh
 * token, so the caller must keep the one it already has.
 */
export async function refreshAccessToken(
  refreshToken: string,
  config: AppConfig = getConfig(),
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<string> {
  const { ok, json } = await postJson(
    issuerUrls(config).refresh,
    { refresh_token: refreshToken },
    { Authorization: `Bearer ${refreshToken}` },
    fetchImpl,
  )

  if (!ok) throw new RefreshFailedError()

  const accessToken = readString(asRecord(json), 'access_token')
  if (accessToken === null) throw new RefreshFailedError()
  return accessToken
}

// ── store ──────────────────────────────────────────────────────────────────

interface AuthState {
  /** In memory only — never persisted (06 §5). */
  accessToken: string | null
  /** Persisted — in `sessionStorage`, or `localStorage` when remembered (06 §5). */
  refreshToken: string | null
  user: AuthUser | null
  /**
   * The login screen's "Remember me".
   *
   * Controls whether the refresh token survives **closing the browser** — not
   * whether it survives a reload. See `authStorage` below: a reload must never
   * cost the operator their session, checkbox or no checkbox.
   */
  remember: boolean
  setSession: (session: AuthSession, remember?: boolean) => void
  setAccessToken: (accessToken: string) => void
  clear: () => void
}

export const AUTH_STORAGE_KEY = 'pigeonly.physical-mail.auth'

/** True when a serialized store snapshot asked to be remembered. */
function remembers(serialized: string): boolean {
  const snapshot = JSON.parse(serialized) as { state?: { remember?: boolean } }
  return snapshot.state?.remember === true
}

/**
 * Where the refresh token lives — chosen per write by "Remember me" (06 §5).
 *
 * `sessionStorage` is the half that matters. It survives a **reload**, which is
 * what "a reload does not force a re-login mid-shift" requires, and dies with
 * the tab, so the next person at a shared workstation does not inherit the
 * session. "Remember me" promotes it to `localStorage`, which also survives
 * closing the browser.
 *
 * This first shipped persisting nothing at all unless remembered, which met the
 * shared-workstation goal by breaking the other one: every reload bounced the
 * operator to `/login` mid-job. Reported from production, 2026-07-30.
 *
 * Only ever one of the two holds the key — whichever is written clears the other,
 * so toggling the checkbox on a later login cannot leave a stale copy behind.
 */
export const authStorage: StateStorage = {
  getItem: (name) => localStorage.getItem(name) ?? sessionStorage.getItem(name),
  setItem: (name, value) => {
    const [keep, drop] = remembers(value)
      ? [localStorage, sessionStorage]
      : [sessionStorage, localStorage]
    keep.setItem(name, value)
    drop.removeItem(name)
  },
  removeItem: (name) => {
    localStorage.removeItem(name)
    sessionStorage.removeItem(name)
  },
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      remember: false,
      setSession: (session, remember = false) =>
        set({
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          user: session.user,
          remember,
        }),
      // Refresh returns no new refresh token, so only the access token moves.
      setAccessToken: (accessToken) => set({ accessToken }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null, remember: false }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => authStorage),
      // The access token must NEVER be persisted; keeping it in memory is the
      // whole point (06 §5). Everything else is, so a reload resumes the session
      // — `authStorage` decides *where*, and therefore how long it lives.
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        user: state.user,
        remember: state.remember,
      }),
    },
  ),
)

/** Claims of the current access token, or null when signed out. */
export function currentClaims(): TokenClaims | null {
  const token = useAuthStore.getState().accessToken
  return token === null ? null : decodeToken(token)
}

export function isSignedIn(): boolean {
  return useAuthStore.getState().refreshToken !== null
}

export function currentUserIsAdmin(): boolean {
  return isAdmin(currentClaims())
}

/**
 * Every identifier the signed-in user is known by.
 *
 * The api identifies people inconsistently: a job's `assignee_id` is a UUID,
 * the token's `user_id` is a decimal string, and the login response carries
 * both again in its own shapes. Rather than pick one and be wrong wherever the
 * api chose the other, ownership checks match against all of them (see
 * `isOwnedBy` in features/workspace/workspaceMode).
 */
export function currentUserIdentifiers(): readonly string[] {
  const claims = currentClaims()
  const user = useAuthStore.getState().user
  return [claims?.userUuid, claims?.userId, user?.uuid, user?.id].filter(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  )
}

export function currentUserIsTranscriber(): boolean {
  return isTranscriber(currentClaims())
}

/** Should the access token be refreshed before its next use? (06 R1) */
export function accessTokenNeedsRefresh(nowMs: number = Date.now()): boolean {
  const state = useAuthStore.getState()
  if (state.refreshToken === null) return false
  if (state.accessToken === null) return true
  return isExpiringSoon(decodeToken(state.accessToken), nowMs)
}
